import { Injectable } from '@angular/core';
import { ListModel } from '@app/_models/listmodel';
import { TranslateService } from '@ngx-translate/core';
import { find } from 'lodash-es';
import { range } from 'lodash-es';
import moment from 'moment-timezone';
import { TranslationService } from './translation.service';


@Injectable({
  providedIn: 'root'
})
export class IanaTimezoneService {
  public ianaTzOptions: ListModel[] = [];

  constructor(
    private translationService: TranslationService,
    private translateService: TranslateService
  ) {
    this.getTimeZones();
  }

  getTimeZones() {
    this.translationService.getTimezoneStaticTranslations(this.translateService.currentLang).subscribe(translations => {
      let timezones = []

      translations.forEach(t => {
        timezones.push(t.id.replace('timezone_iana_', ''));
        t.id = t.id.replace('timezone_iana_', '');
      });

      this.ianaTzOptions = moment.tz.names()
      // Filter out the not wanted timezones
      .filter((tzName: string) => {
        return timezones.includes(tzName.toLowerCase())
      })
      // Get offset for each timezone
      .reduce((tzObjList: any[], tzName: string) => {
        tzObjList.push({
          name: tzName,
          offset: moment.tz(tzName).utcOffset()
        });

        return tzObjList;
      }, [])
      // Sort by the offset
      .sort((a, b) => {
        return a.offset - b.offset
      })
      // Construct ListModel array that has the official iana string as id
      // and a name translated to users language.
      .reduce((ianaTzOptions: ListModel[], tzObj) => {
        let timezone = tzObj.offset ? moment.tz(tzObj.name).format('Z') : '';

        ianaTzOptions.push({
          id: tzObj.name,
          name: `(GMT${timezone}) ${find(translations, t => t.id == tzObj.name.toLowerCase()).name}`
        });

        return ianaTzOptions;
      }, []);
    });
  }

  /**
   * Returns the UTC offset in minutes, for the current date or the given date, of the given timezone
   * @param tzName Iana Timezone name. Defaults to 'Europe/Helsinki'.
   * @param date The date from when the offset should be checked. If date isn't provided the offset is checked from the current time.
   * @param useUtc If true the method will use UTC values of the date object. This is used when the local values of the date object are not true.
   * Example when the data comes from the database and is not inputted by the user.
   */
  getTzOffset(tzName: string, date?: Date|string, useUtc?: boolean): number {
    if(!tzName || tzName?.length < 1) tzName = 'Europe/Helsinki';
    if(typeof date === 'string') date = new Date(date);

    if(date) {
      let year = useUtc ? this.insertLeadingZeros(date.getUTCFullYear(), 4) : this.insertLeadingZeros(date.getFullYear(), 4);
      let month = useUtc ? this.insertLeadingZeros(date.getUTCMonth() + 1) : this.insertLeadingZeros(date.getMonth() + 1);
      let day = useUtc ? this.insertLeadingZeros(date.getUTCDate()) : this.insertLeadingZeros(date.getDate());
      let hours = useUtc ? this.insertLeadingZeros(date.getUTCHours()) : this.insertLeadingZeros(date.getHours());
      let minutes = useUtc ? this.insertLeadingZeros(date.getUTCMinutes()) : this.insertLeadingZeros(date.getMinutes());
      let seconds = useUtc ? this.insertLeadingZeros(date.getUTCSeconds()) : this.insertLeadingZeros(date.getSeconds());
      let milliseconds = useUtc ? this.insertLeadingZeros(date.getUTCMilliseconds(), 3) : this.insertLeadingZeros(date.getMilliseconds(), 3);
      let dateString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
      let tz = moment.tz(dateString, tzName);

      return tz.utcOffset();
    }
    else return moment.tz(tzName).utcOffset();
  }

  /**
   * Inserts leading zeroes when needed.
   * @param timeValue A number value that needs the leading zeroes
   * @param targetLength A length required for the timeValue. Leading zeroes are applied until this length is achieved. Defaults to 2.
   */
  insertLeadingZeros(timeValue: number, targetLength?: number): string {
    if(!targetLength) targetLength = 2;

    return timeValue.toString().length > 1 ? timeValue.toString() : `${'0'.repeat(targetLength - timeValue.toString().length)}${timeValue}`;
  }

  /**
   * Change the timezone of a date object or a date string.
   * Original datetime values are not changed, the method only sets the timezone for it.
   *
   * ex. 2021-05-12T09:00:00.000+03:00 becomes 2021-05-12T09:00:00.000-05:30 when given a timezone with -05:30 UTC offset
   *
   * @param date Date which to convert
   * @param tzName Iana Timezone name. Defaults to 'Europe/Helsinki'.
   */
  setTimeZone(date: string|Date, tzName: string): Date|null {
    if(!date) return null; // If no date is given then return null
    if(!tzName || tzName?.length < 1) tzName = 'Europe/Helsinki'; // Use Helsinki as default timezone if no proper timezone is given
    if(typeof date === 'string') date = new Date(date); // If the date is given as a string then create a date object out of that string

    // Get the offset of the wanted timezone
    const tzOffset: number = this.getTzOffset(tzName, date);

    // Get full hours
    let hours = Math.trunc(Math.abs(tzOffset) / 60).toString();
    hours = hours.length == 1 ? '0' + hours : hours;

    // Get minutes
    let minutes = (Math.abs(tzOffset) % 60).toString();
    minutes = minutes.length == 1 ? '0' + minutes : minutes;

    // Get prefix
    let prefix = Math.sign(tzOffset) == -1 ? '-' : '+';

    // Get the date data
    let dateYear = date.getFullYear();
    let dateMonth = (date.getMonth() + 1).toString().length == 1 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1;
    let dateDay = date.getDate().toString().length == 1 ? '0' + date.getDate() : date.getDate();
    let dateHours = date.getHours().toString().length == 1 ? '0' + date.getHours() : date.getHours();
    let dateMinutes = date.getMinutes().toString().length == 1 ? '0' + date.getMinutes() : date.getMinutes();
    let dateSeconds = date.getSeconds().toString().length == 1 ? '0' + date.getSeconds() : date.getSeconds();
    let dateMilliseconds = date.getMilliseconds().toString().length == 1 ? '00' + date.getMilliseconds() :
                          (date.getMilliseconds().toString().length == 2 ? '0' + date.getMilliseconds() : date.getMilliseconds());

    // Construct a new date string with the given tz offset
    const convertedDateString = `${dateYear}-${dateMonth}-${dateDay}T${dateHours}:${dateMinutes}:${dateSeconds}.${dateMilliseconds}${prefix}${hours}:${minutes}`

    // Finally create a date object from the constructed date string
    let result = new Date(convertedDateString);

    return result;
  }

  /**
   * Returns the time of the day in the given timezone as a string (HH:MM).
   * @param date Date which to convert
   * @param tzName Iana Timezone name. Defaults to 'Europe/Helsinki'.
   */
  getTimeZoneTime(date: string|Date, tzName): string|null {
    if(date == null) return null;
    if(!tzName || tzName?.length < 1) tzName = 'Europe/Helsinki';
    if(typeof date === 'string') date = new Date(date);

    let hours = date.getUTCHours();
    let minutes = date.getUTCMinutes();

    const offset = this.getTzOffset(tzName, date, true);
    const negativeOffset = Math.sign(offset) == -1 ? true : false;

    hours += Math.trunc(offset / 60);

    if(negativeOffset) {
      minutes -= offset % 60;
    }
    else {
      minutes += offset % 60;
    }

    // If the minutes have gone over the hour
    if(minutes > 59) {
      let surplusMinutes = minutes - 60; // Subtract an hour from minutes
      minutes = surplusMinutes; // Take the remaining minutes
      hours += 1; // Add the subtracted hour to hours
    }
    // If the minutes have gone below zero (to the previous hour)
    else if (Math.sign(minutes) == -1) {
      hours -= 1; // Subtract one hour from hours
      minutes = 60 + minutes // Calculate the correct minutes by adding the negative minutes to the full hour minutes (60mins)
    }

    // If the hours have gone to or past the midnight
    if(hours > 23) {
      let surplusHours = hours - 24; // Subtract one day from hours
      hours = surplusHours; // Take the remaining hours
    }
    // If the hours have gone below zero (to the previous day)
    else if(Math.sign(hours) == -1) {
      hours = 24 + hours // Calculate the correct hours by adding the negative hours to the full day hours (24hrs)
    }

    return `${ hours < 10 ? '0' + hours : hours }:${ minutes < 10 ? '0' + minutes : minutes }`;
  }

  /**
   * Calculates the correct date for the date object based on the timezone given.
   * Because Date object really understands only local and utc times and datepicker converts to local
   * time the local time, the date needs to be set manually based on the given timezone.
   * @param date Date which to convert
   * @param tzName Iana Timezone name
   */
  setTimeZoneDate(date: string|Date, tzName: string) {
    if(date == null) return null;
    if(!tzName || tzName?.length < 1) tzName = 'Europe/Helsinki';
    if(typeof date === 'string') date = new Date(date);

    let hours = date.getUTCHours();
    let minutes = date.getUTCMinutes();

    const offset = this.getTzOffset(tzName, date, true);

    const negativeOffset = Math.sign(offset) == -1 ? true : false;

    let dateSwitchedTz = 0; // Has the date switched. -1 = Backwards, 0 = Stayed the same, 1 = Forward.
    let dateSwitchedLocal = 0; // Has the local date switched.

    // Check if there is any offset
    if(offset != 0) {
      hours += Math.trunc(offset / 60);

      if(negativeOffset) {
        minutes -= offset % 60;
      }
      else {
        minutes += offset % 60;
      }

      // If the minutes have gone over the hour
      if(minutes > 59) {
        hours += 1; // Add the subtracted hour to hours
      }
      // If the minutes have gone below zero (to the previous hour)
      else if (Math.sign(minutes) == -1) {
        hours -= 1; // Subtract one hour from hours
      }

      // If the hours have gone to or past the midnight
      if(hours > 23) {
        dateSwitchedTz = 1
      }
      // If the hours have gone below zero (to the previous day)
      else if(Math.sign(hours) == -1) {
        dateSwitchedTz = -1;
      }
    }

    // Check how is the local time compared to UTC time
    if(date.getDate() != date.getUTCDate()) {
      if(date.getDate() > date.getUTCDate()) {
        if(date.getMonth() < date.getUTCMonth()) {
          dateSwitchedLocal = -1 // Date is higher and month is smaller (1.4 --> 30.3)
        }
        else if(date.getMonth() > date.getUTCMonth()) {
          dateSwitchedLocal = -1; // Date is higher and month is higher (1.1.2021 --> 31.12.2020)
        }
        else {
          dateSwitchedLocal = 1; // Date higher and month same (15.3 --> 16.3)
        }
      }
      else if(date.getDate() < date.getUTCDate()) {
        if(date.getMonth() < date.getUTCMonth()) {
          dateSwitchedLocal = 1 // Date is smaller and month is smaller (30.12.2020 --> 1.1.2021)
        }
        else if(date.getMonth() > date.getUTCMonth()) {
          dateSwitchedLocal = 1; // Date is smaller and month is higher (30.1 --> 1.2)
        }
        else {
          dateSwitchedLocal = -1; // Date smaller and month same (15.3 --> 14.3)
        }
      }
    }

    // Set the new date based on the difference between the how much dates are switched on local time and the given timezone time.
    if(dateSwitchedLocal == dateSwitchedTz) return date;
    else if(dateSwitchedLocal > dateSwitchedTz) date.setDate(date.getDate() - (range(dateSwitchedTz, dateSwitchedLocal).length));
    else date.setDate(date.getDate() + (range(dateSwitchedLocal, dateSwitchedTz).length));

    return date;
  }
}
